/**
 * dates.js — calendar days as 'YYYY-MM-DD' strings in the kitchen's LOCAL time, plus
 * the formatting the screens use. Day strings compare and sort as plain strings.
 */

/** Local calendar date as 'YYYY-MM-DD' (never UTC — the kitchen's day, not the server's). */
export function localDateISO(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function daysAgoISO(n, from = new Date()) {
  const d = new Date(from);
  d.setDate(d.getDate() - n);
  return localDateISO(d);
}

/** Whole days from `fromISO` to `toISO` (both 'YYYY-MM-DD'); 0 on bad input. */
export function daysBetween(fromISO, toISO) {
  if (!fromISO || !toISO) return 0;
  const a = new Date(`${fromISO}T00:00:00`);
  const b = new Date(`${toISO}T00:00:00`);
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return 0;
  return Math.round((b - a) / 86400000);
}

/** `iso` moved by `n` whole days (negative = earlier); noon anchor keeps DST days whole. */
export function shiftISO(iso, n) {
  return localDateISO(new Date(new Date(`${iso}T12:00:00`).getTime() + n * 86400000));
}

export function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

export function fmtDateShort(iso) {
  if (!iso) return '—';
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Firestore Timestamp | {seconds} | ISO string → "3:45 PM". */
export function fmtTime(v) {
  if (!v) return '';
  let d;
  if (typeof v === 'string') d = new Date(v);
  else if (v?.toDate) d = v.toDate();
  else if (v?.seconds) d = new Date(v.seconds * 1000);
  else return '';
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** Sort key for a possibly-Firestore timestamp — bigger = more recent. */
export function timeMs(v) {
  if (!v) return 0;
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isNaN(t) ? 0 : t; }
  if (v?.seconds) return v.seconds * 1000;
  if (v?.toDate) return v.toDate().getTime();
  return 0;
}

/**
 * Is `iso` inside [from, to]? Either bound may be '' (open-ended) — so an empty
 * `from` with a set `to` means "everything up to `to`".
 */
export function inDateRange(iso, from = '', to = '') {
  if (!iso) return false;
  if (from && iso < from) return false;
  if (to && iso > to) return false;
  return true;
}

/** Where `iso` sits inside [start, end] as a 0–100% offset (clamped) — the strip's x-axis. */
export function datePosition(iso, start, end) {
  const span = daysBetween(start, end);
  if (span <= 0) return 100;
  return Math.max(0, Math.min(100, (daysBetween(start, iso) / span) * 100));
}

// Firestore's free daily quota resets at midnight Pacific time.
const QUOTA_TZ = 'America/Los_Angeles';
const HOUR_MS = 3600000;
const pacificClock = d => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: QUOTA_TZ, hourCycle: 'h23', hour: 'numeric', minute: 'numeric', second: 'numeric',
  }).formatToParts(d);
  const get = type => Number((parts.find(p => p.type === type) || {}).value || 0);
  return { h: get('hour'), m: get('minute'), s: get('second') };
};

/**
 * When the database quota next resets (midnight Pacific), as this device's clock time —
 * "2:00 AM" in Central time. '' if the browser can't tell time zones apart.
 */
export function quotaResetTime(now = new Date()) {
  try {
    const { h, m, s } = pacificClock(now);
    let reset = now.getTime() - ((h * 60 + m) * 60 + s) * 1000 - now.getMilliseconds() + 24 * HOUR_MS;
    // A 23- or 25-hour day (daylight saving starts / ends) lands an hour off midnight.
    const at = pacificClock(new Date(reset)).h;
    if (at === 23) reset += HOUR_MS;
    else if (at === 1) reset -= HOUR_MS;
    return new Date(reset).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}
